import React, { useEffect, useState, useMemo } from 'react';
import { usePaymentStore, useCariStore, useAuthStore, getAuthAxios } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  CheckCircle,
  CalendarDays,
  Download,
  Loader2,
  AlertTriangle,
  Clock,
  MessageCircle,
  Send,
  TrendingUp,
  Banknote,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useI18n } from '@/components/I18nProvider';

const formatCurrency = (value, currency = 'TRY') => {
  const symbols = { TRY: 'TL', USD: '$', EUR: '€' };
  const formatted = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(value);
  return `${symbols[currency] || currency}${formatted}`;
};

const getStatusBadge = (status, daysRemaining) => {
  const statusConfig = {
    pending: { label: 'Bekliyor', className: 'bg-status-pending/10 text-status-pending border-status-pending/20' },
    overdue: { label: 'Gecikti', className: 'bg-destructive/10 text-destructive border-destructive/20' },
    paid: { label: 'Ödendi', className: 'bg-status-paid/10 text-status-paid border-status-paid/20' },
    cancelled: { label: 'İptal', className: '' },
  };
  const config = statusConfig[status] || statusConfig.pending;
  return <Badge variant={status === 'cancelled' ? 'secondary' : 'default'} className={config.className}>{config.label}</Badge>;
};

const getRemainingText = (daysRemaining, status) => {
  if (status === 'paid') return null;
  if (status === 'cancelled') return null;
  if (daysRemaining === 0) return <span className="text-xs font-medium text-amber-600">Bugün</span>;
  if (daysRemaining < 0) return <span className="text-xs font-medium text-destructive">{Math.abs(daysRemaining)} gün gecikti</span>;
  if (daysRemaining <= 3) return <span className="text-xs font-medium text-amber-600">{daysRemaining} gün kaldı</span>;
  return <span className="text-xs text-muted-foreground">{daysRemaining} gün kaldı</span>;
};

const PAYMENT_METHODS = ['Nakit', 'EFT/Havale', 'Kredi Kartı', 'Vadeli Çek'];

// Searchable Cari Select Component
const CariSearchSelect = ({ caris, value, onValueChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return caris;
    const q = search.toLowerCase();
    return caris.filter((c) => c.title.toLowerCase().includes(q) || c.phone?.includes(q));
  }, [caris, search]);

  const selectedCari = caris.find((c) => c.id === value);

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between font-normal"
        onClick={() => setOpen(!open)}
        data-testid="payment-cari-select"
      >
        <span className={!selectedCari ? 'text-muted-foreground' : ''}>
          {selectedCari ? selectedCari.title : 'Cari seçin'}
        </span>
        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full rounded-md border bg-popover shadow-lg" data-testid="cari-dropdown">
          <div className="p-2">
            <Input
              placeholder="Cari ara (ad veya telefon)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              data-testid="cari-search-input"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">Cari bulunamadı</div>
            ) : (
              filtered.map((cari) => (
                <button
                  key={cari.id}
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                    value === cari.id && 'bg-accent font-medium'
                  )}
                  onClick={() => { onValueChange(cari.id); setOpen(false); setSearch(''); }}
                  data-testid={`cari-option-${cari.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{cari.title}</div>
                    <div className="truncate text-xs text-muted-foreground">{cari.phone}</div>
                  </div>
                  {value === cari.id && <CheckCircle className="h-4 w-4 shrink-0 text-primary" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function PaymentsPage() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { payments, isLoading, fetchPayments, createPayment, updatePayment, markAsPaid, deletePayment } = usePaymentStore();
  const { caris, fetchCaris } = useCariStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    cari_id: '',
    amount: '',
    currency: 'TRY',
    due_date: '',
    note: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Collection popup state
  const [collectDialogOpen, setCollectDialogOpen] = useState(false);
  const [collectingPayment, setCollectingPayment] = useState(null);
  const [collectMethod, setCollectMethod] = useState('Nakit');
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [conflictData, setConflictData] = useState(null);

  useEffect(() => {
    fetchPayments();
    fetchCaris();
  }, [fetchPayments, fetchCaris]);

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      payment.cari_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.note?.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeTab === 'pending') {
      if (!(payment.status === 'pending' || payment.status === 'overdue')) return false;
    } else if (activeTab === 'today') {
      const dueDate = payment.due_date?.split('T')[0];
      if (!(dueDate === todayStr && (payment.status === 'pending' || payment.status === 'overdue'))) return false;
    }

    // Date filter
    if (dateFrom) {
      const dueDate = payment.due_date?.split('T')[0];
      if (dueDate < dateFrom) return false;
    }
    if (dateTo) {
      const dueDate = payment.due_date?.split('T')[0];
      if (dueDate > dateTo) return false;
    }

    return matchesSearch;
  }).sort((a, b) => {
    const dateA = a.due_date ? new Date(a.due_date).getTime() : Infinity;
    const dateB = b.due_date ? new Date(b.due_date).getTime() : Infinity;
    return dateA - dateB;
  });

  const openCollectDialog = (payment) => {
    setCollectingPayment(payment);
    setCollectMethod('Nakit');
    setCollectDialogOpen(true);
  };

  const handleCollect = async () => {
    if (!collectingPayment) return;
    try {
      const { getAuthAxios } = await import('@/store');
      const api = getAuthAxios();
      await api.post(`/payments/${collectingPayment.id}/mark-paid`, { payment_method: collectMethod });
      toast.success(`Tahsil edildi (${collectMethod})`);
      setCollectDialogOpen(false);
      setCollectingPayment(null);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tahsilat hatası');
    }
  };

  const handleOpenDialog = (payment = null) => {
    if (payment) {
      setEditingPayment(payment);
      const dueDate = payment.due_date ? payment.due_date.split('T')[0] : '';
      setFormData({
        cari_id: payment.cari_id,
        amount: payment.amount.toString(),
        currency: payment.currency,
        due_date: dueDate,
        note: payment.note || '',
      });
    } else {
      setEditingPayment(null);
      setFormData({ cari_id: '', amount: '', currency: 'TRY', due_date: '', note: '' });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPayment(null);
    setConflictData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = {
      cari_id: formData.cari_id,
      amount: parseFloat(formData.amount),
      currency: formData.currency,
      due_date: formData.due_date,
      note: formData.note,
      send_whatsapp: sendWhatsApp,
    };

    let result;
    if (editingPayment) {
      result = await updatePayment(editingPayment.id, data);
      if (result.success) toast.success(t('paymentUpdated'));
    } else {
      result = await createPayment(data);
      if (result.conflict) {
        setConflictData({ ...result.existing_payment, formData: data });
        setIsSubmitting(false);
        return;
      }
      if (result.success) {
        toast.success(sendWhatsApp ? t('paymentCreated') + ' + WhatsApp' : t('paymentCreated'));
      }
    }

    setIsSubmitting(false);
    if (result.success) {
      handleCloseDialog();
    } else {
      toast.error(result.error);
    }
  };

  const handleForceCreate = async () => {
    if (!conflictData?.formData) return;
    setIsSubmitting(true);
    const result = await createPayment({ ...conflictData.formData, force_create: true });
    setIsSubmitting(false);
    setConflictData(null);
    if (result.success) {
      toast.success(sendWhatsApp ? t('paymentCreated') + ' + WhatsApp' : t('paymentCreated'));
      handleCloseDialog();
    } else {
      toast.error(result.error);
    }
  };

  const handleMarkPaid = async (id) => {
    const result = await markAsPaid(id);
    if (result.success) {
      toast.success('Ödeme tahsil edildi olarak işaretlendi');
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu ödeme sözünü silmek istediğinize emin misiniz?')) {
      const result = await deletePayment(id);
      if (result.success) {
        toast.success('Ödeme sözü silindi');
      } else {
        toast.error(result.error);
      }
    }
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Cari', t('amount'), t('dueDate'), t('status'), 'Not', 'Kalan Gün'];
    const rows = filteredPayments.map((p) => [
      p.id.slice(0, 8), p.cari_title || '', `${p.amount} ${p.currency}`,
      format(new Date(p.due_date), 'dd.MM.yyyy'), p.status, p.note || '', p.days_remaining,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'odeme-sozleri.csv';
    link.click();
  };

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const [sendingReminder, setSendingReminder] = useState(null);

  const handleSendReminder = async (payment) => {
    if (!payment.cari_phone) return toast.error('Müşteri telefon numarası bulunamadı');
    setSendingReminder(payment.id);
    try {
      const api = getAuthAxios();
      await api.post(`/payments/${payment.id}/send-reminder`);
      toast.success(t('reminderSent'));
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('messageFailed'));
    }
    setSendingReminder(null);
  };
  const pendingCount = payments.filter((p) => p.status === 'pending' || p.status === 'overdue').length;
  const todayCount = payments.filter((p) => {
    const dueDate = p.due_date?.split('T')[0];
    return dueDate === todayStr && (p.status === 'pending' || p.status === 'overdue');
  }).length;

  // Analytics
  const analytics = useMemo(() => {
    const pending = payments.filter((p) => p.status === 'pending');
    const overdue = payments.filter((p) => p.status === 'overdue');
    const paid = payments.filter((p) => p.status === 'paid');
    const all = payments.filter((p) => p.status === 'pending' || p.status === 'overdue');
    const totalPending = all.reduce((s, p) => s + p.amount, 0);
    const totalOverdue = overdue.reduce((s, p) => s + p.amount, 0);
    const totalPaid = paid.reduce((s, p) => s + p.amount, 0);
    const todayDue = payments.filter((p) => {
      const dd = p.due_date?.split('T')[0];
      return dd === todayStr && (p.status === 'pending' || p.status === 'overdue');
    });
    const totalToday = todayDue.reduce((s, p) => s + p.amount, 0);
    // Messages
    const msgSent = all.filter((p) => p.last_message_status === 'sent').length;
    const msgNotSent = all.filter((p) => !p.last_message_status || p.last_message_status === 'failed').length;
    return { totalPending, totalOverdue, totalPaid, totalToday, pendingCount: pending.length, overdueCount: overdue.length, paidCount: paid.length, todayDueCount: todayDue.length, msgSent, msgNotSent };
  }, [payments, todayStr]);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Ödeme Takibi</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ödeme sözlerini takip edin</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-payments-btn">
            <Download className="mr-2 h-4 w-4" />
            Dışa Aktar
          </Button>
          <Button size="sm" onClick={() => handleOpenDialog()} data-testid="add-payment-btn">
            <Plus className="mr-2 h-4 w-4" />
            Ödeme Sözü
          </Button>
        </div>
      </div>

      {/* Analytics Stats */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5" data-testid="payment-stats">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <div className="shrink-0 rounded-lg bg-amber-100 p-2"><Clock className="h-4 w-4 text-amber-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Bekleyen Toplam</p>
                <p className="font-heading text-sm sm:text-lg font-bold truncate">{formatCurrency(analytics.totalPending)}</p>
                <p className="text-[10px] text-muted-foreground">{analytics.pendingCount + analytics.overdueCount} adet</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <div className="shrink-0 rounded-lg bg-red-100 p-2"><AlertTriangle className="h-4 w-4 text-red-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Vadesi Geçen</p>
                <p className="font-heading text-sm sm:text-lg font-bold truncate text-destructive">{formatCurrency(analytics.totalOverdue)}</p>
                <p className="text-[10px] text-muted-foreground">{analytics.overdueCount} adet</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <div className="shrink-0 rounded-lg bg-blue-100 p-2"><CalendarDays className="h-4 w-4 text-blue-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Bugün Vadeli</p>
                <p className="font-heading text-sm sm:text-lg font-bold truncate">{formatCurrency(analytics.totalToday)}</p>
                <p className="text-[10px] text-muted-foreground">{analytics.todayDueCount} adet</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <div className="shrink-0 rounded-lg bg-green-100 p-2"><TrendingUp className="h-4 w-4 text-green-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Tahsil Edilen</p>
                <p className="font-heading text-sm sm:text-lg font-bold truncate text-green-700">{formatCurrency(analytics.totalPaid)}</p>
                <p className="text-[10px] text-muted-foreground">{analytics.paidCount} adet</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <div className="shrink-0 rounded-lg bg-purple-100 p-2"><MessageCircle className="h-4 w-4 text-purple-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Mesaj Durumu</p>
                <p className="font-heading text-sm sm:text-lg font-bold truncate">{analytics.msgSent} <span className="text-xs font-normal text-muted-foreground">gönderildi</span></p>
                <p className="text-[10px] text-muted-foreground">{analytics.msgNotSent} gönderilmedi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Date Filter */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Ödeme sözü ara..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-payments"
              />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[130px] text-xs" data-testid="payment-date-from" />
              <span className="text-xs text-muted-foreground">-</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[130px] text-xs" data-testid="payment-date-to" />
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setDateFrom(''); setDateTo(''); }}>Temizle</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs & Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="pending" className="gap-2" data-testid="tab-pending">
            <Clock className="h-4 w-4" />
            Bekleyenler ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="today" className="gap-2" data-testid="tab-today">
            <CalendarDays className="h-4 w-4" />
            Bugün ({todayCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
            </div>
          ) : filteredPayments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {activeTab === 'today'
                  ? 'Bugün vadesi gelen ödeme yok'
                  : searchTerm ? 'Arama sonucu bulunamadı' : t('noPayments')}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="space-y-5 md:hidden">
                {filteredPayments.map((payment) => (
                  <Card
                    key={payment.id}
                    className={cn(
                      'transition-all',
                      payment.status === 'overdue' && 'border-destructive/30 bg-destructive/5',
                      payment.days_remaining === 0 && payment.status === 'pending' && 'border-status-pending/30 bg-status-pending/5'
                    )}
                    data-testid={`payment-card-${payment.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold truncate">{payment.cari_title}</span>
                            {getStatusBadge(payment.status, payment.days_remaining)}
                          </div>
                          {payment.cari_phone && (
                            <p className="mt-0.5 text-xs text-muted-foreground">{payment.cari_phone}</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-lg">{formatCurrency(payment.amount, payment.currency)}</p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <CalendarDays className="h-3.5 w-3.5" />
                            <span>{format(new Date(payment.due_date), 'dd.MM.yyyy')}</span>
                          </div>
                          <div className={cn(
                            'flex items-center gap-1 font-medium text-xs',
                            payment.days_remaining < 0 && 'text-destructive',
                            payment.days_remaining === 0 && 'text-amber-600',
                            payment.days_remaining > 0 && payment.days_remaining <= 3 && 'text-amber-600',
                            payment.days_remaining > 3 && 'text-muted-foreground'
                          )}>
                            {getRemainingText(payment.days_remaining, payment.status)}
                          </div>
                          {/* Message status */}
                          <div className="flex items-center gap-1 text-xs" data-testid={`msg-status-mobile-${payment.id}`}>
                            <MessageCircle className={cn('h-3 w-3', payment.last_message_status === 'sent' ? 'text-green-600' : payment.last_message_status === 'failed' ? 'text-red-500' : payment.last_message_status === 'pending' ? 'text-amber-500' : 'text-muted-foreground/40')} />
                            <span className={cn(payment.last_message_status === 'sent' ? 'text-green-600' : payment.last_message_status === 'failed' ? 'text-red-500' : payment.last_message_status === 'pending' ? 'text-amber-500' : 'text-muted-foreground')}>
                              {payment.last_message_status === 'sent' ? 'Gönderildi' : payment.last_message_status === 'failed' ? 'Başarısız' : payment.last_message_status === 'pending' ? t('statusPending') : 'Gönderilmedi'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          {(payment.status === 'pending' || payment.status === 'overdue') && payment.cari_phone && (
                            <Button
                              variant="ghost" size="icon" className="h-8 w-8"
                              onClick={() => handleSendReminder(payment)}
                              disabled={sendingReminder === payment.id}
                              title="Hatırlat"
                              data-testid={`remind-btn-${payment.id}`}
                            >
                              {sendingReminder === payment.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-blue-600" />}
                            </Button>
                          )}
                          {(payment.status === 'pending' || payment.status === 'overdue') && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openCollectDialog(payment)} data-testid={`mark-paid-${payment.id}`}>
                              <CheckCircle className="h-4 w-4 text-status-paid" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(payment)} data-testid={`edit-payment-${payment.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(payment.id)} data-testid={`delete-payment-${payment.id}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {payment.note && (
                        <p className="mt-2 text-xs text-muted-foreground border-t pt-2">{payment.note}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <Card className="hidden md:block">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Müşteri</TableHead>
                        <TableHead className="text-right">{t('amount')}</TableHead>
                        <TableHead>{t('dueDate')}</TableHead>
                        <TableHead>Kalan Süre</TableHead>
                        <TableHead>{t('status')}</TableHead>
                        <TableHead>{t('messageText')}</TableHead>
                        <TableHead className="hidden lg:table-cell">Not</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPayments.map((payment) => (
                        <TableRow
                          key={payment.id}
                          className={cn(
                            payment.status === 'overdue' && 'bg-destructive/5',
                            payment.days_remaining === 0 && payment.status === 'pending' && 'bg-status-pending/5'
                          )}
                          data-testid={`payment-row-${payment.id}`}
                        >
                          <TableCell>
                            <div>
                              <div className="font-medium">{payment.cari_title}</div>
                              <div className="text-xs text-muted-foreground">{payment.cari_phone}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium whitespace-nowrap">
                            {formatCurrency(payment.amount, payment.currency)}
                          </TableCell>
                          <TableCell>{format(new Date(payment.due_date), 'dd.MM.yyyy')}</TableCell>
                          <TableCell>
                            {getRemainingText(payment.days_remaining, payment.status)}
                          </TableCell>
                          <TableCell>{getStatusBadge(payment.status, payment.days_remaining)}</TableCell>
                          <TableCell data-testid={`msg-status-${payment.id}`}>
                            <div className="flex items-center gap-1.5">
                              <MessageCircle className={cn('h-3.5 w-3.5', payment.last_message_status === 'sent' ? 'text-green-600' : payment.last_message_status === 'failed' ? 'text-red-500' : payment.last_message_status === 'pending' ? 'text-amber-500' : 'text-muted-foreground/40')} />
                              <div className="flex flex-col">
                                <span className={cn('text-xs font-medium', payment.last_message_status === 'sent' ? 'text-green-600' : payment.last_message_status === 'failed' ? 'text-red-500' : payment.last_message_status === 'pending' ? 'text-amber-500' : 'text-muted-foreground')}>
                                  {payment.last_message_status === 'sent' ? 'Gönderildi' : payment.last_message_status === 'failed' ? 'Başarısız' : payment.last_message_status === 'pending' ? t('statusPending') : 'Gönderilmedi'}
                                </span>
                                {payment.cari_phone && <span className="text-[10px] text-muted-foreground">{payment.cari_phone}</span>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell max-w-[200px] truncate">{payment.note || '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {(payment.status === 'pending' || payment.status === 'overdue') && payment.cari_phone && (
                                <Button
                                  variant="ghost" size="icon"
                                  onClick={() => handleSendReminder(payment)}
                                  disabled={sendingReminder === payment.id}
                                  title="Hatırlat"
                                  data-testid={`remind-btn-${payment.id}`}
                                >
                                  {sendingReminder === payment.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-blue-600" />}
                                </Button>
                              )}
                              {(payment.status === 'pending' || payment.status === 'overdue') && (
                                <Button variant="ghost" size="icon" onClick={() => openCollectDialog(payment)} title="Tahsil Et" data-testid={`mark-paid-${payment.id}`}>
                                  <CheckCircle className="h-4 w-4 text-status-paid" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(payment)} data-testid={`edit-payment-${payment.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(payment.id)} data-testid={`delete-payment-${payment.id}`}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingPayment ? 'Ödeme Sözü Düzenle' : t('addPayment')}</DialogTitle>
            <DialogDescription>
              {editingPayment ? 'Ödeme sözü bilgilerini güncelleyin' : 'Yeni bir ödeme sözü oluşturun'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Cari *</Label>
              <CariSearchSelect
                caris={caris}
                value={formData.cari_id}
                onValueChange={(value) => setFormData({ ...formData, cari_id: value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Tutar *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="payment-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Para Birimi</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                  <SelectTrigger data-testid="payment-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="due_date">Vade Tarihi *</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
                className="block"
                data-testid="payment-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="note">Not</Label>
              <Input
                id="note"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Ödeme sözü hakkında not"
                data-testid="payment-note-input"
              />
            </div>
            {!editingPayment && (
              <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/30" data-testid="send-whatsapp-toggle">
                <Checkbox
                  id="sendWhatsApp"
                  checked={sendWhatsApp}
                  onCheckedChange={(checked) => setSendWhatsApp(checked)}
                  data-testid="send-whatsapp-checkbox"
                />
                <Label htmlFor="sendWhatsApp" className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                  <MessageCircle className="h-4 w-4 text-green-600" />
                  WhatsApp'tan hatırlatma mesajı gönder
                </Label>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>{t('cancel')}</Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.cari_id || !formData.amount || !formData.due_date}
                data-testid="save-payment-btn"
              >
                {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Kaydediliyor...</> : editingPayment ? t('update') : 'Oluştur'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Collect Payment Dialog */}
      <Dialog open={collectDialogOpen} onOpenChange={setCollectDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="collect-dialog">
          <DialogHeader>
            <DialogTitle>Tahsilat Yöntemi Seçin</DialogTitle>
            <DialogDescription>
              {collectingPayment && (
                <>
                  <span className="font-medium">{collectingPayment.cari_title}</span> - {formatCurrency(collectingPayment.amount, collectingPayment.currency)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {PAYMENT_METHODS.map((method) => (
              <Button
                key={method}
                variant={collectMethod === method ? 'default' : 'outline'}
                className={cn('h-auto py-3 text-sm', collectMethod === method && 'ring-2 ring-primary')}
                onClick={() => setCollectMethod(method)}
                data-testid={`method-${method.replace(/\//g, '-').toLowerCase()}`}
              >
                {method}
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollectDialogOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleCollect} data-testid="confirm-collect-btn">
              <CheckCircle className="mr-2 h-4 w-4" />
              Tahsil Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict Warning Dialog */}
      <Dialog open={!!conflictData} onOpenChange={(open) => { if (!open) setConflictData(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Bekleyen Ödeme Sözü Var
            </DialogTitle>
            <DialogDescription>
              Bu carinin zaten bekleyen bir ödeme sözü bulunmaktadır. Yine de yeni bir ödeme sözü oluşturmak istiyor musunuz?
            </DialogDescription>
          </DialogHeader>
          {conflictData && (
            <div className="rounded-lg border bg-amber-50 p-4 space-y-1.5" data-testid="existing-payment-info">
              <p className="text-sm font-medium">Mevcut Ödeme Sözü:</p>
              <p className="text-sm">Tutar: <span className="font-semibold">{formatCurrency(conflictData.amount, conflictData.currency)}</span></p>
              <p className="text-sm">Vade: <span className="font-semibold">{conflictData.due_date}</span></p>
              {conflictData.note && <p className="text-sm text-muted-foreground">Not: {conflictData.note}</p>}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConflictData(null)}>{t('cancel')}</Button>
            <Button onClick={handleForceCreate} disabled={isSubmitting} data-testid="force-create-btn">
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Devam Et, Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
